<template>
  <div
    class="integration-collection-card"
    :class="{ 'integration-collection-card--inactive': status && status !== 'active' }"
    role="button"
    tabindex="0"
    @click="$emit('open')"
    @keydown.enter="$emit('open')"
    @keydown.space.prevent="$emit('open')"
  >
    <!-- Stacked previews (folder look) -->
    <div class="icc__stack">
      <div
        v-for="(src, i) in stackPreviews"
        :key="i"
        class="icc__stack-layer"
        :class="`icc__stack-layer--${i}`"
        :style="src ? `background-image:url(${src})` : ''"
      >
        <i
          v-if="!src"
          class="fa-light fa-layer-group"
        />
      </div>

      <!-- Partner logo badge -->
      <div class="icc__logo">
        <img
          v-if="icon"
          :src="icon"
          :alt="name"
        >
        <i
          v-else
          class="fa-solid fa-plug"
        />
      </div>

      <!-- Count chip -->
      <span class="icc__count-chip">{{ count }}</span>
    </div>

    <!-- Body -->
    <div class="icc__body">
      <span class="icc__kicker">{{ resolvedKicker }}</span>
      <h3 class="icc__name">{{ name }}</h3>
      <span class="icc__count-label">{{ countLabel }}</span>
    </div>

    <!-- Hover overlay -->
    <div class="icc__overlay">
      <span
        v-if="status && status !== 'active'"
        class="icc__install"
      >
        <i class="fa-light fa-circle-down" /> {{ installLabel }}
      </span>
      <span class="icc__open">
        {{ openLabel }} <i class="fa-light fa-arrow-right" />
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'IntegrationCollectionCard',
  props: {
    name: { type: String, required: true },
    icon: { type: String, default: null },
    count: { type: Number, default: 0 },
    status: { type: String, default: null },
    previews: { type: Array as () => string[], default: () => [] },
    // Défaut null → résolu via i18n dans le composant (G9, plus de 'PAR' figé).
    kicker: { type: String, default: null },
    countLabel: { type: String, default: '' },
    openLabel: { type: String, default: '' },
    installLabel: { type: String, default: '' },
  },
  emits: ['open'],
  setup(props) {
    const { t } = useI18n();

    // Kicker par défaut traduit (« PAR » / « BY ») quand la prop n'est pas fournie.
    const resolvedKicker = computed(() => props.kicker || t('integrations.collection.kicker'));

    // Always render 3 stack layers (fill missing with empty placeholders).
    const stackPreviews = computed(() => {
      const out = (props.previews || []).slice(0, 3);
      while (out.length < 3) out.push('');
      // Top layer first in DOM stacking order.
      return out;
    });
    return { stackPreviews, resolvedKicker };
  },
});
</script>

<style scoped>
.integration-collection-card {
  position: relative;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e6e9ef;
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  height: 100%;
  min-height: 280px;
  transition: box-shadow 0.18s ease, transform 0.18s ease, border-color 0.18s ease;
}
.integration-collection-card:hover {
  border-color: #c7b6f3;
  box-shadow: 0 10px 28px rgba(99, 70, 196, 0.16);
  transform: translateY(-2px);
}
.integration-collection-card:focus-visible {
  outline: 2px solid #8b5cf6;
  outline-offset: 2px;
}

/* Stacked previews */
.icc__stack {
  position: relative;
  height: 150px;
  margin: 14px 14px 0;
}
.icc__stack-layer {
  position: absolute;
  left: 0;
  right: 0;
  border-radius: 10px;
  background-size: cover;
  background-position: center;
  background-color: #f3f4f8;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c2c8d4;
  font-size: 22px;
  border: 1px solid #ececf2;
}
.icc__stack-layer--2 { top: 0; height: 96px; transform: scale(0.86); opacity: 0.55; z-index: 1; }
.icc__stack-layer--1 { top: 14px; height: 112px; transform: scale(0.93); opacity: 0.8; z-index: 2; }
.icc__stack-layer--0 { top: 30px; height: 120px; z-index: 3; box-shadow: 0 4px 12px rgba(20, 30, 50, 0.08); }

.icc__logo {
  position: absolute;
  bottom: -14px;
  left: 14px;
  z-index: 5;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #e6e9ef;
  box-shadow: 0 4px 10px rgba(20, 30, 50, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.icc__logo img { width: 100%; height: 100%; object-fit: contain; }
.icc__logo i { color: #8b5cf6; font-size: 16px; }

.icc__count-chip {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 5;
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  border-radius: 12px;
  background: #6346c4;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Body */
.icc__body {
  padding: 22px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}
.icc__kicker {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8b5cf6;
}
.icc__name {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #1f2430;
  line-height: 1.25;
}
.icc__count-label {
  margin-top: 2px;
  font-size: 12.5px;
  color: #7a8294;
}

/* Hover overlay */
.icc__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(99, 70, 196, 0) 40%, rgba(99, 70, 196, 0.92) 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px;
  opacity: 0;
  transition: opacity 0.18s ease;
}
.integration-collection-card:hover .icc__overlay,
.integration-collection-card:focus-visible .icc__overlay { opacity: 1; }
.icc__open {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #fff;
  color: #6346c4;
  font-size: 13px;
  font-weight: 600;
  padding: 9px 16px;
  border-radius: 8px;
}
.icc__install {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: rgba(255, 255, 255, 0.18);
  padding: 4px 10px;
  border-radius: 99px;
}
.integration-collection-card--inactive .icc__stack { filter: grayscale(0.35); opacity: 0.92; }
</style>
